import React, { useState } from "react";
// ANT DESIGN COMPONENTS
import { Button, Tooltip, Collapse } from "antd";
// FONT AWESOME LIBRARY AND ICONS
import { faPlus, faTrash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
// REDUX
import { useSelector } from "react-redux";
// I18N TRANSLATION
import { useTranslation } from "react-i18next";
// INTERFACES
import {
  GroupLiftInterface,
  GroupInterface,
  LocationInterface,
  SiteInterface,
} from "../../../../../interfaces/AdapterCdoOpc.interface";
import { RootState } from "../../../../../app/store";
// COMPONENTS
import FormInputText from "../../../../components/form/FormInputText";
import FormInputNumber from "../../../../components/form/FormInputNumber";

export default function Template({
  setCdoOpcLocation,
  location,
}: {
  setCdoOpcLocation: (newValue: LocationInterface) => void;
  location: LocationInterface;
}) {
  // ************************************************ */
  // GLOBAL VARIABLES ******************************* */
  const [t] = useTranslation("global");
  const { theme } = useSelector((state: RootState) => state.home);
  const { Panel } = Collapse;

  // ************************************************ */
  // USE STATE VARIABLES **************************** */
  const [site, setSite] = useState<SiteInterface>({ name: "", groups: [] });
  const [group, setGroup] = useState<GroupInterface>({
    group_name: "",
    path_name: "",
    num_floors: 2,
    lifts: [],
  });
  const [lift, setLift] = useState<GroupLiftInterface>({
    name: "",
    num_floors: 2,
  });

  // ************************************************ */
  // REDUX SLICE VARIABLES ************************** */

  // ************************************************ */
  // CUSTOM HOOKS *********************************** */

  // ************************************************ */
  // SERVICES AND API CALLS ************************* */

  // ************************************************ */
  // FUNCTIONS ************************************** */
  const onClickAddNewSite = () => {
    const copy = structuredClone(location);
    copy.sites.push(site);
    setCdoOpcLocation(copy);
    setSite({ name: "", groups: [] });
  };

  const onClickAddNewGroup = () => {
    const copy = structuredClone(site);
    copy.groups.push(group);
    setSite(copy);
    setGroup({ group_name: "", path_name: "", num_floors: 2, lifts: [] });
  };

  const onClickAddNewLift = () => {
    const copy = structuredClone(group);
    copy.lifts.push(lift);
    setGroup(copy);
    setLift({ name: "", num_floors: 2 });
  };

  const onChangeLift = (newValue: string | number, variableName: string) => {
    const copy = structuredClone(lift);
    if (variableName === "name" && typeof newValue === "string") {
      copy.name = newValue;
    } else if (variableName === "num_floors" && typeof newValue === "number") {
      copy.num_floors = newValue;
    }
    setLift(copy);
  };

  const onChangeGroup = (newValue: string | number, variableName: string) => {
    const copy = structuredClone(group);
    if (variableName === "group_name" && typeof newValue === "string") {
      copy.group_name = newValue;
    } else if (variableName === "path_name" && typeof newValue === "string") {
      copy.path_name = newValue;
    } else if (variableName === "num_floors" && typeof newValue === "number") {
      copy.num_floors = newValue;
    }
    setGroup(copy);
  };

  const onChangeSite = (newValue: string, variableName: string) => {
    const copy = structuredClone(site);
    if (variableName === "name") {
      copy.name = newValue;
    }
    setSite(copy);
  };

  const onChangeLocation = (newValue: string, variableName: string) => {
    const copy = structuredClone(location);
    if (variableName === "name") {
      copy.name = newValue;
    }
    setCdoOpcLocation(copy);
  };

  const onClickRemoveLift = (index: number) => {
    const copy = structuredClone(group);
    copy.lifts.splice(index, 1);
    setGroup(copy);
  };

  const onClickRemoveGroup = (index: number) => {
    const copy = structuredClone(site);
    copy.groups.splice(index, 1);
    setSite(copy);
  };

  const onClickRemoveSite = (index: number) => {
    const copy = structuredClone(location);
    copy.sites.splice(index, 1);
    setCdoOpcLocation(copy);
  };

  // ************************************************ */
  // USE EFFECT ************************************* */

  // ************************************************ */
  // COMPONENT ************************************** */
  return (
    <div>
      {/** ********************************************************************* */}
      {/** LOCATION */}
      <br />
      <Collapse>
        <Panel header={t("general.opcLocation")} key="1">
          <div className="container">
            <div className={`drawer__box__${theme} row`}>
              <FormInputText
                inputValue={location.name}
                inputLabelName={t("general.locationName")}
                inputOnChange={onChangeLocation}
                inputVariableName="name"
                isRequired
                disabledInput={false}
              />
              <div className="col-md-12 mt-3">
                <div className="generalStyles__flex">
                  <div className="generalStyles__inputFlexNameLarge">
                    <span className="generalStyles__inputFlexRequired">*</span>
                    <span className="drawer__text">{t("general.sites")}</span>
                  </div>
                  <div className="generalStyles__width100">
                    {location.sites.map(
                      (siteLocation: SiteInterface, siteLocationIndex: number) => (
                        <div
                          key={siteLocation.name}
                          className="mb-3 generalStyles__flex"
                          style={{
                            backgroundColor: "#1f1f1f",
                            borderRadius: "5px",
                            fontSize: "10px",
                            padding: "10px",
                          }}
                        >
                          <div style={{ width: "190px" }}>
                            <div className="generalStyles__textEllipsis">
                              <span className="generalStyles__warning generalStyles__mrFix">
                                {t("general.name")}
                              </span>
                              : <Tooltip title={siteLocation.name}>{siteLocation.name}</Tooltip>
                            </div>
                            {siteLocation.groups.map((groupSiteLocation: GroupInterface) => (
                              <div
                                key={groupSiteLocation.group_name}
                                className="mb-3 generalStyles__flex"
                              >
                                <div style={{ width: "200px" }}>
                                  <div className="generalStyles__textEllipsis">
                                    <span
                                      className="generalStyles__info generalStyles__mrFix"
                                      style={{ marginLeft: "10px" }}
                                    >
                                      {t("general.name")}
                                    </span>
                                    :
                                    <Tooltip title={groupSiteLocation.group_name}>
                                      {groupSiteLocation.group_name}
                                    </Tooltip>
                                  </div>
                                  <div className="generalStyles__textEllipsis">
                                    <span
                                      className="generalStyles__info generalStyles__mrFix"
                                      style={{ marginLeft: "10px" }}
                                    >
                                      {t("general.pathName")}
                                    </span>
                                    :{" "}
                                    <Tooltip title={groupSiteLocation.path_name}>
                                      {groupSiteLocation.path_name}
                                    </Tooltip>
                                  </div>
                                  <div>
                                    <span
                                      className="generalStyles__info generalStyles__mrFix"
                                      style={{ marginLeft: "10px" }}
                                    >
                                      {t("general.numberOfFloors")}
                                    </span>
                                    : {groupSiteLocation.num_floors}
                                  </div>
                                  {groupSiteLocation.lifts.map(
                                    (liftGroupSiteLocation: GroupLiftInterface) => (
                                      <div
                                        key={liftGroupSiteLocation.name}
                                        className="mb-1 generalStyles__flex"
                                      >
                                        <div style={{ width: "200px" }}>
                                          <div className="generalStyles__textEllipsis">
                                            <span
                                              className="generalStyles__success generalStyles__mrFix"
                                              style={{ marginLeft: "20px" }}
                                            >
                                              {t("general.name")}
                                            </span>
                                            :
                                            <Tooltip title={liftGroupSiteLocation.name}>
                                              {liftGroupSiteLocation.name}
                                            </Tooltip>
                                          </div>
                                          <div>
                                            <span
                                              className="generalStyles__success generalStyles__mrFix"
                                              style={{ marginLeft: "20px" }}
                                            >
                                              {t("general.numberOfFloors")}
                                            </span>
                                            : {liftGroupSiteLocation.num_floors}
                                          </div>
                                        </div>
                                      </div>
                                    ),
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                          <div className="generalStyles__flexCenter" style={{ width: "50px" }}>
                            <Tooltip title={t("general.deleteSite")}>
                              <Button
                                onClick={() => {
                                  onClickRemoveSite(siteLocationIndex);
                                }}
                                className="buttonStyle__29"
                                shape="circle"
                                icon={<FontAwesomeIcon icon={faTrash} />}
                              />
                            </Tooltip>
                          </div>
                        </div>
                      ),
                    )}
                  </div>
                  <div className="generalStyles__infoTooltipIconBlank" />
                </div>
              </div>
            </div>
          </div>
          {/** ********************************************************************* */}
          {/** LOCATION SITES */}
          <br />
          <div className="container">
            <div className={`drawer__box__${theme} row`}>
              <FormInputText
                inputValue={site.name}
                inputLabelName={t("general.siteName")}
                inputOnChange={onChangeSite}
                inputVariableName="name"
                isRequired
                disabledInput={false}
              />
              <div className="col-md-12 mt-3">
                <div className="generalStyles__flex">
                  <div className="generalStyles__inputFlexNameLarge">
                    <span className="generalStyles__inputFlexRequired">*</span>
                    <span className="drawer__text">{t("general.groups")}</span>
                  </div>
                  <div className="generalStyles__width100">
                    {site.groups.map((groupSite: GroupInterface, groupSiteIndex: number) => (
                      <div
                        key={groupSite.group_name}
                        className="mb-3 generalStyles__flex"
                        style={{
                          backgroundColor: "#1f1f1f",
                          borderRadius: "5px",
                          fontSize: "10px",
                          padding: "10px",
                        }}
                      >
                        <div style={{ width: "190px" }}>
                          <div className="generalStyles__textEllipsis">
                            <span className="generalStyles__info generalStyles__mrFix">
                              {t("general.name")}
                            </span>
                            : <Tooltip title={groupSite.group_name}>{groupSite.group_name}</Tooltip>
                          </div>
                          <div className="generalStyles__textEllipsis">
                            <span className="generalStyles__info generalStyles__mrFix">
                              {t("general.pathName")}
                            </span>
                            : <Tooltip title={groupSite.path_name}>{groupSite.path_name}</Tooltip>
                          </div>
                          <div>
                            <span className="generalStyles__info generalStyles__mrFix">
                              {t("general.numberOfFloors")}
                            </span>
                            : {groupSite.num_floors}
                          </div>
                          {groupSite.lifts.map((liftGroupSite: GroupLiftInterface) => (
                            <div key={liftGroupSite.name} className="mb-1 generalStyles__flex">
                              <div style={{ width: "200px" }}>
                                <div className="generalStyles__textEllipsis">
                                  <span
                                    className="generalStyles__success generalStyles__mrFix "
                                    style={{ marginLeft: "10px" }}
                                  >
                                    {t("general.name")}
                                  </span>
                                  :{" "}
                                  <Tooltip title={liftGroupSite.name}>
                                    <span>{liftGroupSite.name}</span>
                                  </Tooltip>
                                </div>
                                <div>
                                  <span
                                    className="generalStyles__success generalStyles__mrFix "
                                    style={{ marginLeft: "10px" }}
                                  >
                                    {t("general.numberOfFloors")}
                                  </span>
                                  : {liftGroupSite.num_floors}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                        <div className="generalStyles__flexCenter" style={{ width: "50px" }}>
                          <Tooltip title={t("general.deleteGroup")}>
                            <Button
                              onClick={() => {
                                onClickRemoveGroup(groupSiteIndex);
                              }}
                              className="buttonStyle__29"
                              shape="circle"
                              icon={<FontAwesomeIcon icon={faTrash} />}
                            />
                          </Tooltip>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="generalStyles__infoTooltipIconBlank" />
                </div>
              </div>
              <div className="col-md-12 mt-3">
                <div className="generalStyles__flex">
                  <div className="generalStyles__inputFlexNameLarge">
                    <span className="generalStyles__inputFlexRequired" />
                  </div>
                  <div className="generalStyles__width100">
                    <Button
                      onClick={onClickAddNewSite}
                      disabled={site.name === ""}
                      className={`generalStyles__width100 ${
                        site.name === "" ? "" : "buttonStyle__28"
                      }`}
                    >
                      <FontAwesomeIcon icon={faPlus} className="generalStyles__mrFix" />
                      {t("general.add")}
                    </Button>
                  </div>
                  <div className="generalStyles__infoTooltipIconBlank" />
                </div>
              </div>
            </div>
          </div>
          {/** ********************************************************************* */}
          {/** LOCATION GROUP */}
          <br />
          <div className="container">
            <div className={`drawer__box__${theme} row`}>
              <FormInputText
                inputValue={group.group_name}
                inputLabelName={t("general.groupName")}
                inputOnChange={onChangeGroup}
                inputVariableName="group_name"
                isRequired
                disabledInput={false}
              />
              <FormInputText
                inputValue={group.path_name}
                inputLabelName={t("general.pathName")}
                inputOnChange={onChangeGroup}
                inputVariableName="path_name"
                isRequired
                disabledInput={false}
              />
              <FormInputNumber
                inputValue={group.num_floors}
                inputOnChange={onChangeGroup}
                inputLabelName={t("general.numberOfFloors")}
                inputVariableName="num_floors"
              />
              <div className="col-md-12 mt-3">
                <div className="generalStyles__flex">
                  <div className="generalStyles__inputFlexNameLarge">
                    <span className="generalStyles__inputFlexRequired">*</span>
                    <span className="drawer__text">{t("general.lifts")}</span>
                  </div>
                  <div className="generalStyles__width100">
                    {group.lifts.map((liftGroup: GroupLiftInterface, liftGroupIndex: number) => (
                      <div
                        key={liftGroup.name}
                        className="mb-3 generalStyles__flex"
                        style={{
                          backgroundColor: "#1f1f1f",
                          borderRadius: "5px",
                          fontSize: "10px",
                          padding: "10px",
                        }}
                      >
                        <div
                          style={{
                            width: "200px",
                          }}
                        >
                          <div className="generalStyles__textEllipsis">
                            <span className="generalStyles__success generalStyles__mrFix">
                              {t("general.name")}
                            </span>
                            :{" "}
                            <Tooltip title={liftGroup.name}>
                              <span>{liftGroup.name}</span>
                            </Tooltip>
                          </div>
                          <div>
                            <span className="generalStyles__success generalStyles__mrFix">
                              {t("general.numberOfFloors")}
                            </span>
                            : {liftGroup.num_floors}
                          </div>
                        </div>
                        <div className="generalStyles__flexCenter" style={{ width: "50px" }}>
                          <Tooltip title={t("general.deleteLift")}>
                            <Button
                              onClick={() => {
                                onClickRemoveLift(liftGroupIndex);
                              }}
                              className="buttonStyle__29"
                              shape="circle"
                              icon={<FontAwesomeIcon icon={faTrash} />}
                            />
                          </Tooltip>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="generalStyles__infoTooltipIconBlank" />
                </div>
              </div>
              <div className="col-md-12 mt-3">
                <div className="generalStyles__flex">
                  <div className="generalStyles__inputFlexNameLarge">
                    <span className="generalStyles__inputFlexRequired" />
                  </div>
                  <div className="generalStyles__width100">
                    <Button
                      onClick={onClickAddNewGroup}
                      disabled={
                        group.group_name === "" ||
                        group.path_name === "" ||
                        group.num_floors <= 1 ||
                        group.num_floors > 100
                      }
                      className={`generalStyles__width100 ${
                        group.group_name === "" ||
                        group.path_name === "" ||
                        group.num_floors <= 1 ||
                        group.num_floors > 100
                          ? ""
                          : "buttonStyle__28"
                      }`}
                    >
                      <FontAwesomeIcon icon={faPlus} className="generalStyles__mrFix" />
                      {t("general.add")}
                    </Button>
                  </div>
                  <div className="generalStyles__infoTooltipIconBlank" />
                </div>
              </div>
            </div>
          </div>
          {/** ********************************************************************* */}
          {/** LOCATION LIFT */}
          <br />
          <div className="container">
            <div className={`drawer__box__${theme} row`}>
              <FormInputText
                inputValue={lift.name}
                inputLabelName={t("general.liftName")}
                inputOnChange={onChangeLift}
                inputVariableName="name"
                isRequired
                disabledInput={false}
              />
              <FormInputNumber
                inputValue={lift.num_floors}
                inputOnChange={onChangeLift}
                inputLabelName={t("general.numberOfFloors")}
                inputVariableName="num_floors"
              />
              <div className="col-md-12 mt-3">
                <div className="generalStyles__flex">
                  <div className="generalStyles__inputFlexNameLarge">
                    <span className="generalStyles__inputFlexRequired" />
                  </div>
                  <div className="generalStyles__width100">
                    <Button
                      onClick={onClickAddNewLift}
                      disabled={lift.name === "" || lift.num_floors <= 1 || lift.num_floors > 100}
                      className={`generalStyles__width100 ${
                        lift.name === "" || lift.num_floors <= 1 || lift.num_floors > 100
                          ? ""
                          : "buttonStyle__28"
                      }`}
                    >
                      <FontAwesomeIcon icon={faPlus} className="generalStyles__mrFix" />
                      {t("general.add")}
                    </Button>
                  </div>
                  <div className="generalStyles__infoTooltipIconBlank" />
                </div>
              </div>
            </div>
          </div>
        </Panel>
      </Collapse>

      <br />
    </div>
  );
}
